// Background ingest-job persistence for the Catalog Manager.
//
// A "job" is one run of the ingest research chain over a batch of products. We
// track jobs so they can run in the background, survive a tab/browser close,
// and be re-opened/reviewed later.
//
// ============================================================================
// STORAGE NOTE (read me before extending):
// For now jobs live in the browser's localStorage, keyed per signed-in user.
// This is deliberately a stop-gap so we don't add a DB table yet. All reads and
// writes go through `loadJobs` / `saveJobs` below — keep it that way so the day
// we move this server-side (a `catalog_ingest_runs` table keyed by customer +
// execution id), it's a single swap of these two functions rather than a hunt
// across the UI. The execution id we store IS the EasyAI chain execution id, so
// it already lines up with a future backend record and with easyai run history.
// ============================================================================
import { IngestReviewItem } from "./types";
import { ChainProgress } from "./useCatalogApi";

export type IngestJobStatus =
  | "starting"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

export interface IngestJob {
  /** Local id (stable across the job's life; equals executionId once known). */
  id: string;
  /** EasyAI chain execution id — the link back to run history on easyai. */
  executionId?: string;
  chainId: string;
  status: IngestJobStatus;
  /** Human label for the batch (first product + count). */
  label: string;
  /** How many products were submitted. */
  submittedCount: number;
  /** How many review items came back (once completed). */
  resultCount?: number;
  createdAt: number;
  updatedAt: number;
  credits?: number | null;
  progress?: ChainProgress | null;
  error?: string;
  /** The review items produced by the run (persisted so runs are re-viewable). */
  resultItems?: IngestReviewItem[];
  summary?: Record<string, unknown>;
}

/** Max concurrent in-flight jobs before we make the user wait. */
export const MAX_CONCURRENT_JOBS = 5;

/** Keep at most this many jobs in storage (newest kept); older ones drop. */
const MAX_STORED_JOBS = 25;

const LS_PREFIX = "catalogManager.ingestJobs";

// Scope storage to the signed-in user so one machine's users don't see each
// other's runs. Falls back to a shared key when no user id is available.
let userScope = "anon";
export const setIngestJobsUserScope = (id?: string): void => {
  userScope =
    (id || "anon").replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 64) || "anon";
};
const storageKey = (): string => `${LS_PREFIX}.${userScope}`;

const isTerminal = (s: IngestJobStatus): boolean =>
  s === "completed" || s === "failed" || s === "cancelled";

/** Read all jobs for the current user (newest first). See STORAGE NOTE above. */
export const loadJobs = (): IngestJob[] => {
  try {
    const raw = window.localStorage.getItem(storageKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as IngestJob[])
      .filter((j) => j && typeof j.id === "string")
      .sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
};

/**
 * Persist the full job list. See STORAGE NOTE above. We cap the count and, if we
 * hit the localStorage quota (result items can be large), progressively shed the
 * heaviest payloads (oldest terminal jobs' resultItems) before giving up — the
 * job metadata is far more important to keep than a re-viewable old payload.
 */
export const saveJobs = (jobs: IngestJob[]): void => {
  const trimmed = [...jobs]
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, MAX_STORED_JOBS);

  const attempt = (list: IngestJob[]): boolean => {
    try {
      window.localStorage.setItem(storageKey(), JSON.stringify(list));
      return true;
    } catch {
      return false;
    }
  };

  if (attempt(trimmed)) return;

  // Quota hit — drop resultItems from the oldest terminal jobs first, then any.
  const lighten = trimmed
    .map((j) => ({ ...j }))
    .sort((a, b) => a.createdAt - b.createdAt);
  for (const j of lighten) {
    if (isTerminal(j.status) && j.resultItems) {
      delete j.resultItems;
      delete j.summary;
      const reordered = [...lighten].sort((a, b) => b.createdAt - a.createdAt);
      if (attempt(reordered)) return;
    }
  }
  // Last resort: keep just the most recent few, metadata only.
  attempt(
    trimmed.slice(0, 5).map(({ resultItems, summary, ...rest }) => {
      void resultItems;
      void summary;
      return rest;
    })
  );
};

/** A short, friendly label for a submitted batch. */
export const labelForBatch = (foodInput: string, count: number): string => {
  const firstLine = (foodInput || "")
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  let head = firstLine || "products";
  // If the first line is JSON, try to pull a name out of it.
  if (head.startsWith("{") || head.startsWith("[")) {
    try {
      const parsed = JSON.parse(foodInput);
      const first = Array.isArray(parsed) ? parsed[0] : parsed;
      head = (first?.name || first?.brand || "products").toString();
    } catch {
      head = "products";
    }
  }
  if (head.length > 40) head = `${head.slice(0, 40)}…`;
  return count > 1 ? `${head} +${count - 1} more` : head;
};

export const activeJobCount = (jobs: IngestJob[]): number =>
  jobs.filter((j) => j.status === "starting" || j.status === "running").length;
